import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  computed,
  inject,
  signal,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { SesionService } from '@intelishs-group/session-core';
import { DecimalPipe } from '@angular/common';
import { EmpleadoService } from '@services/empleado.service';
import { VacacionesService } from '@services/vacaciones.service';
import { SaldoVacaciones } from '@schemas/vacaciones.schema';

interface AccesoRapido {
  key: string;
  ruta: string;
  titulo: string;
  descripcion: string;
  icono: string;
  color: string;
  /** Roles canónicos autorizados. 'all' = visible para todos. */
  /**
   * Visible SIEMPRE, tenga o no el modulo otorgado. Lo lleva solo «Solicitar
   * Vacaciones», que es lo que antes era `roles: 'all'`. Los tres roles de RH lo
   * tienen otorgado igualmente, asi que en la practica no cambia nada: se
   * conserva para no quitarle a nadie la unica pantalla a la que entra todo el
   * mundo.
   */
  siempre?: true;
}

const ACCESOS: AccesoRapido[] = [
  {
    key: 'dashboard',
    ruta: '/dashboard',
    titulo: 'Dashboard',
    descripcion: 'Resumen ejecutivo de Recursos Humanos.',
    icono: 'dashboard',
    color: 'blue',
  },
  {
    key: 'empleados',
    ruta: '/empleados',
    titulo: 'Empleados',
    descripcion: 'Gestión y consulta del personal.',
    icono: 'contacts',
    color: 'purple',
  },
  {
    key: 'solicitar-vacaciones',
    ruta: '/solicitar-vacaciones',
    titulo: 'Solicitar Vacaciones',
    descripcion: 'Crear o consultar tus solicitudes.',
    icono: 'carry-out',
    color: 'teal',
    siempre: true,
  },
  {
    key: 'mis-autorizaciones',
    ruta: '/mis-autorizaciones',
    titulo: 'Mis Autorizaciones',
    descripcion: 'Aprobar solicitudes de tu equipo.',
    icono: 'like',
    color: 'orange',
  },
  {
    key: 'autorizaciones-rh',
    ruta: '/autorizaciones-rh',
    titulo: 'Autorizaciones RH',
    descripcion: 'Aprobaciones finales de RH.',
    icono: 'audit',
    color: 'green',
  },
  {
    key: 'vacaciones',
    ruta: '/vacaciones',
    titulo: 'Vacaciones',
    descripcion: 'Listado global de solicitudes.',
    icono: 'schedule',
    color: 'magenta',
  },
  {
    key: 'calendario',
    ruta: '/calendario',
    titulo: 'Calendario',
    descripcion: 'Vista mensual de vacaciones.',
    icono: 'calendar',
    color: 'cyan',
  },
];

import { EmptyState } from '@intelishs-group/ui';

@Component({
  selector: 'app-bienvenida',
  imports: [DecimalPipe, EmptyState, NzIconModule, NzSkeletonModule],
  templateUrl: './bienvenida.page.html',
  styleUrl: './bienvenida.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BienvenidaPage implements OnInit {
  private readonly router = inject(Router);
  private readonly empleadoService = inject(EmpleadoService);
  private readonly vacacionesService = inject(VacacionesService);
  private readonly sesion = inject(SesionService);
  private readonly destroyRef = inject(DestroyRef);

  // ── Estado del usuario ────────────────────────────────────────────
  readonly nombre = signal<string>('');
  readonly idInterno = signal<string | null>(null);
  readonly rol = signal<string>('');
  readonly cargandoEmpleado = signal<boolean>(true);

  /**
   * «Buenos días, María» según la hora de México. Con el PRIMER nombre: el
   * completo en letra grande se lee como encabezado de expediente. Mismo
   * saludo que el inicio de fadminportal.
   */
  readonly saludo = computed(() => {
    const hora = Number(
      new Intl.DateTimeFormat('es-MX', {
        hour: 'numeric',
        hourCycle: 'h23',
        timeZone: 'America/Mexico_City',
      }).format(new Date()),
    );
    const momento = hora < 12 ? 'Buenos días' : hora < 19 ? 'Buenas tardes' : 'Buenas noches';
    const nombre = this.nombre().trim().split(/\s+/)[0];
    return nombre ? `${momento}, ${nombre}` : momento;
  });

  /** «martes 29 de septiembre», en hora de México. */
  readonly fecha = new Intl.DateTimeFormat('es-MX', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'America/Mexico_City',
  }).format(new Date());

  /**
   * Saldo del propio usuario. Se pinta cuando `getMe()` devuelve expediente,
   * NO cuando el rol es Empleado: la condición honesta es tener el dato, y así
   * también lo ven directores y RH, que igual son empleados con días. Una
   * cuenta de sistema sin expediente —el `admin` que no está dado de alta— no
   * pinta nada y la página se queda como estaba.
   *
   * Para un empleado esto cambia el sentido de la página: sus accesos son UNO,
   * así que sin el saldo la bienvenida era una pantalla intermedia con un solo
   * botón entre él y lo único que viene a hacer.
   */
  readonly saldo = signal<SaldoVacaciones | null>(null);

  // ── Accesos visibles según el MÓDULO otorgado ─────────────────────
  //
  // Antes se traducía el rol del portal ("Administrador RH") a uno de cuatro
  // canónicos y se comparaba contra una lista por tarjeta. Ahora se pregunta por
  // el módulo, y la `key` de cada tarjeta ES su código — comprobado uno por uno
  // contra `acceso.modulo`: los siete coinciden.
  //
  // Es más que cambiar de fuente: esta rejilla y el menú lateral del shell pasan
  // a leer LO MISMO, así que no pueden volver a discrepar. Y un administrador
  // que quite un módulo a un rol desde el portal lo ve aquí sin desplegar nada.
  readonly accesos = computed<AccesoRapido[]>(() =>
    ACCESOS.filter((a) => a.siempre === true || this.sesion.tieneModulo(a.key)),
  );

  ngOnInit(): void {
    // La sesión YA está resuelta: `proveerSesion` la carga antes de montar el
    // primer componente. Aquí había un «camino feliz» síncrono y un fallback con
    // polling por si el bootstrap aún no había poblado el servicio; los dos
    // sobran, y con ellos las 330 líneas que los sostenían.
    this.aplicarSesion();
  }

  irA(ruta: string): void {
    this.router.navigate([ruta]);
  }

  // ─────────────────────────────────────────────────────────────────
  // Privado
  // ─────────────────────────────────────────────────────────────────


  private aplicarSesion(): void {
    this.nombre.set(this.componerNombre());
    this.rol.set(this.sesion.accesoActivo()?.rolNombre ?? '');

    // Sin sesión no se puede consultar /me.
    if (!this.sesion.usuario()) {
      this.cargandoEmpleado.set(false);
      return;
    }

    // /api/Empleado/me hace el JOIN catusuario ↔ catempleadorh y devuelve
    // el empleado del usuario logueado, incluyendo el idInterno (MC...).
    this.empleadoService
      .getMe()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (emp) => {
          this.idInterno.set(emp?.idInterno ?? null);
          // Si el JWT no traía nombre, usar el del empleado.
          if (!this.nombre() && emp?.nombreCompleto) {
            this.nombre.set(emp.nombreCompleto);
          }
          this.cargandoEmpleado.set(false);
          if (emp?.id) this.cargarSaldo(emp.id);
        },
        error: () => {
          // 404 (sin empleado asociado) o backend no responde — mostrar la
          // bienvenida sin el chip MC, sin romper la página.
          this.idInterno.set(null);
          this.cargandoEmpleado.set(false);
        },
      });
  }

  /** El saldo es informativo: si falla, la bienvenida sigue en pie sin él. */
  private cargarSaldo(empleadoId: number): void {
    this.vacacionesService
      .getSaldo(empleadoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (s) => this.saldo.set(s),
        error: () => this.saldo.set(null),
      });
  }

  /**
   * El nombre para saludar.
   *
   * Antes se componía a mano de cuatro claims del JWT —`fullName`, y si no
   * `givenName + firstSurname + secondSurname`, y si no `userName`—. La sesión
   * de plataforma ya trae `nombreCompleto` compuesto, así que aquí sólo queda el
   * respaldo al `login`: un saludo en blanco se lee como sesión rota.
   */
  private componerNombre(): string {
    const u = this.sesion.usuario();
    if (!u) return '';
    return u.nombreCompleto?.trim() || u.login;
  }
}
